/**
 * Pre-render a site's design-system widgets in the sandbox, for publishing or
 * previewing with an uploaded design system: every button, heading, card and
 * input — on every page and in the shared header/footer — rendered by the
 * uploaded code, sanitized, keyed by widget id.
 */
import type { GridWidgetContent, SiteDoc } from "@website-builder/schemas";

import type { DsWidgetDescription } from "../dsSandbox/protocol.js";
import type { DsSandbox } from "../dsSandbox/sandboxHost.js";
import { isDsWidgetKind } from "../theme/dsWidgetElement.js";
import type { ActiveDesignSystem } from "./designSystemFlows.js";

export function collectDsWidgets(doc: SiteDoc): DsWidgetDescription[] {
  const out: DsWidgetDescription[] = [];
  const visit = (grid: GridWidgetContent | undefined) => {
    for (const { widget } of grid?.items ?? []) {
      const content = widget.content as unknown as Record<string, unknown>;
      const kind = String(content["contentType"]);
      // An Inline is a nested grid too: its blocks render through the site's
      // design system like any other.
      if (kind === "grid" || kind === "inline-grid")
        visit(widget.content as unknown as GridWidgetContent);
      else if (isDsWidgetKind(kind))
        out.push({ key: String(widget.id), contentType: kind, content });
    }
  };
  for (const page of doc.pages) visit(page.data.grid as GridWidgetContent);
  const site = doc.site as unknown as {
    header?: GridWidgetContent;
    footer?: GridWidgetContent;
  };
  visit(site.header);
  visit(site.footer);
  return out;
}

export async function prerenderDesignSystem(
  doc: SiteDoc,
  sandbox: DsSandbox,
): Promise<ReadonlyMap<string, string>> {
  const widgets = collectDsWidgets(doc);
  const html = widgets.length ? await sandbox.render(widgets) : {};
  return new Map(Object.entries(html));
}

/**
 * What a publish or preview renders with, given the site's active design
 * system: nothing extra for Duro; the pre-rendered widgets, stylesheet and
 * manifest for an uploaded one. An uploaded one that couldn't be loaded is an
 * error — rendering it in Duro would publish the site wrong (and the server
 * refuses that anyway).
 */
export async function customRenderFor(
  active: ActiveDesignSystem | undefined,
  doc: () => SiteDoc,
): Promise<
  | { readonly ok: true; readonly custom?: CustomRender }
  | { readonly ok: false; readonly error: string }
> {
  if (!active || active.kind === "duro") return { ok: true };
  if (active.kind === "unavailable")
    return {
      ok: false,
      error: `this site's design system couldn't be loaded (${active.reason})`,
    };
  if (active.kind === "needs-consent")
    return {
      ok: false,
      error: `this site uses the design system "${active.manifest.name}" — allow it on this device first`,
    };
  const site = doc();
  const unsupported = collectDsWidgets(site).filter((w) =>
    (active.unsupported as ReadonlySet<string>).has(w.contentType),
  );
  if (unsupported.length > 0)
    return {
      ok: false,
      error: `the design system "${active.manifest.name}" can't render ${[
        ...new Set(unsupported.map((w) => w.contentType)),
      ].join(" and ")} widgets (${unsupported
        .map((w) => w.key)
        .join(", ")}) — upload a newer release, or remove them`,
    };
  return {
    ok: true,
    custom: {
      html: await prerenderDesignSystem(site, active.sandbox),
      css: active.css,
      manifestSha: active.manifestSha,
    },
  };
}

export interface CustomRender {
  readonly html: ReadonlyMap<string, string>;
  readonly css: string;
  readonly manifestSha: string;
}
